import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  addConfiguration,
  editConfiguration,
  getConfigurationById,
  removeConfiguration,
  setConfigurationAsDefault,
} from "../../features/configuration/configurationSlice";
import { Icons } from "../../constants/icon-library";
import {
  configurationInitialValues,
  configurationValidationSchema,
  REASONING_SUPPORTED_MODELS,
  REASONING_EFFORT_OPTIONS,
  chatValidationSchema,
} from "../../constants/model.constants";
import { toast } from "react-toastify";
import {
  GENERAL_LLM_MODELS,
  TYPES,
  ELEVEN_TTS_MODEL_IDS,
  AUDIO_FORMATS,
  LLM_PROVIDERS,
} from "../../constants/assistant.constants";
import { confirmationModal } from "../../constants/confirmation.constants";

const DEFAULT_PROMPT = "You are a helpful assistant.";

const ConfigurationForm = () => {
  const { uuid, agentId, configurationId } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const { selectedConfiguration } = useSelector((state) => state.configuration);

  const configUuidRef = useRef(null);
  const [originalValues, setOriginalValues] = useState(null);
  const { onAssistantUpdated } = useOutletContext() || {};

  const getConfigurationType = (type) => {
    if (type === "chat") return "chat";
    if (type === "voice") return "voice";
    return null;
  };

  useEffect(() => {
    if (configurationId && selectedConfiguration) {
      const configData = selectedConfiguration;
      configUuidRef.current = configData.uuid || configurationId;

      const formValues = {
        name: configData.name || "",
        type: configData.type || "",
        llmProvider: configData.llmProvider || "",
        llmModel: configData.llmModel || "",
        temperature: configData.temperature?.$numberDecimal
          ? parseFloat(configData.temperature.$numberDecimal)
          : configData.temperature || 0.7,
        reasoningEffort: configData.reasoningEffort || "",
        llmApiKey: configData.llmApiKey || "",
        ttsModel: configData.ttsModel || "",
        sttModel: configData.sttModel || "",
        elevenlabsApiKey: configData.elevenlabsApiKey || "",
        agentId: configData.elevenlabsAgentId || "",
        voiceId: configData.voiceId || "",
        modelId: configData.modelId || "",
        speed: typeof configData.speed === "number" ? configData.speed : 1,
        agentOutputAudioFormat: configData.agentOutputAudioFormat || "",
        userInputAudioFormat: configData.userInputAudioFormat || "",
        transcriber: configData.transcriber || "",
        transcriberModel: configData.transcriberModel || "",
        stability: configData.stability || 0.5,
        similarityBoost: configData.similarityBoost || 0.75,
        isDefaultChat: configData.isDefaultChat,
        isDefaultVoice: configData.isDefaultVoice,
        systemPrompt: configData.systemPrompt || DEFAULT_PROMPT,
      };

      formik.setValues(formValues);
      setOriginalValues({ ...formValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConfiguration, configurationId]);

  useEffect(() => {
    if (configurationId) {
      dispatch(getConfigurationById(configurationId));
    } else {
      configUuidRef.current = null;
      const initialFormValues = {
        ...configurationInitialValues,
        llmProvider: "",
        isDefaultChat: false,
        isDefaultVoice: false,
        systemPrompt: DEFAULT_PROMPT,
      };
      formik.setValues(initialFormValues);
      setOriginalValues(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationId, dispatch]);

  const handleDelete = async () => {
    if (!configUuidRef.current) {
      toast.error("No configuration to delete");
      return;
    }

    const result = await confirmationModal({
      title: "Are you sure?",
      text: "This will permanently delete the configuration.",
    });

    if (result.isConfirmed) {
      const action = await dispatch(removeConfiguration(configUuidRef.current));
      if (action.type.endsWith("/fulfilled")) {
        toast.success("Configuration deleted successfully");
        configUuidRef.current = null;
        formik.resetForm();
        navigate(agentId ? `/agent/${agentId}` : `/my-assistants/${uuid}`, {
          state: { activeTab: "configuration" },
        });
        onAssistantUpdated?.();
      } else {
        toast.error("Failed to delete configuration");
      }
    }
  };

  const getProviderSpecificConfig = (values) => {
    const baseConfig = {
      llmProvider: values.llmProvider,
      llmModel: values.llmModel,
      temperature: parseFloat(values.temperature),
      ...(values.reasoningEffort
        ? { reasoningEffort: values.reasoningEffort }
        : {}),
    };

    if (values.type === "chat") {
      return {
        ...baseConfig,
        llmApiKey: values.llmApiKey,
      };
    } else if (values.type === "voice") {
      return {
        ...baseConfig,
        elevenlabsApiKey: values.elevenlabsApiKey,
        agentId: values.agentId,
        voiceId: values.voiceId,
        modelId: values.modelId || "",
        speed: values.speed ? parseFloat(values.speed) : 1,
        agentOutputAudioFormat: values.agentOutputAudioFormat || "",
        userInputAudioFormat: values.userInputAudioFormat || "",
        stability: parseFloat(values.stability),
        similarityBoost: parseFloat(values.similarityBoost),
      };
    }

    return baseConfig;
  };

  const getChangedFields = (currentValues, originalValues) => {
    if (!originalValues) return currentValues;
    const changes = {};
    Object.keys(currentValues).forEach((key) => {
      if (key === "isDefaultChat" || key === "isDefaultVoice") return;
      const currentValue = currentValues[key];
      const originalValue = originalValues[key];
      if (
        typeof currentValue === "number" &&
        typeof originalValue === "number"
      ) {
        if (Math.abs(currentValue - originalValue) > 0.001)
          changes[key] = currentValue;
      } else if (currentValue !== originalValue) {
        changes[key] = currentValue;
      }
    });
    return changes;
  };

  const handleSubmit = async (values) => {
    try {
      let success = false;
      let createdUuid = null;

      if (configUuidRef.current) {
        const changedFields = getChangedFields(values, originalValues);

        if (
          "reasoningEffort" in changedFields &&
          !changedFields.reasoningEffort
        ) {
          delete changedFields.reasoningEffort;
        }

        const action = await dispatch(
          editConfiguration({
            uuid: configUuidRef.current,
            data: changedFields,
          }),
        );
        success = action.payload?.success;
      } else {
        const configurationPayload = {
          name: values.name,
          type: values.type,
          systemPrompt: values.systemPrompt,
          ...getProviderSpecificConfig(values),
        };

        const action = await dispatch(
          addConfiguration({
            ...configurationPayload,
            accountId: user?.org,
            ...(agentId ? { agentId } : { assistantId: uuid }),
          }),
        );
        success = action.payload?.success;
        createdUuid = action.payload?.uuid;
      }

      if (!success) {
        toast.error("Something went wrong!");
        return;
      }

      const configToSetDefault = configUuidRef.current || createdUuid;
      const configurationMode = getConfigurationType(values.type);

      if (
        (configurationMode === "chat" && values.isDefaultChat) ||
        (configurationMode === "voice" && values.isDefaultVoice)
      ) {
        await dispatch(
          setConfigurationAsDefault({
            uuid: configToSetDefault,
            type: configurationMode,
          }),
        );
        toast.success(
          `Configuration saved and set as default ${configurationMode} configuration successfully`,
        );
      } else {
        toast.success("Configuration saved successfully");
      }

      navigate(-1);
      onAssistantUpdated?.();
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }
  };

  const formik = useFormik({
    initialValues: {
      ...configurationInitialValues,
      llmProvider: "",
      systemPrompt: "",
    },
    enableReinitialize: true,
    validationSchema:
      originalValues?.type === "chat"
        ? chatValidationSchema
        : configurationValidationSchema,
    onSubmit: handleSubmit,
  });

  const { setValues } = formik;

  useEffect(() => {
    if (configurationId) {
      dispatch(getConfigurationById(configurationId)).then((res) => {
        const configData = res?.payload;
        if (configData) {
          configUuidRef.current = configData.uuid || configurationId;
          const formValues = {
            name: configData.name || "",
            type: configData.type || "",
            llmProvider: configData.llmProvider || "",
            llmModel: configData.llmModel || "",
            temperature: configData.temperature?.$numberDecimal
              ? parseFloat(configData.temperature.$numberDecimal)
              : configData.temperature || 0.7,
            reasoningEffort: configData.reasoningEffort || "",
            llmApiKey: configData.llmApiKey || "",
            elevenlabsApiKey: configData.elevenlabsApiKey || "",
            agentId: configData.elevenlabsAgentId || "",
            voiceId: configData.voiceId || "",
            modelId: configData.modelId || "",
            speed: typeof configData.speed === "number" ? configData.speed : 1,
            agentOutputAudioFormat: configData.agentOutputAudioFormat || "",
            userInputAudioFormat: configData.userInputAudioFormat || "",
            stability: configData.stability || 0.5,
            similarityBoost: configData.similarityBoost || 0.75,
            isDefaultChat: configData.isDefaultChat,
            isDefaultVoice: configData.isDefaultVoice,
            systemPrompt: configData.systemPrompt || DEFAULT_PROMPT,
          };

          setValues(formValues);
          setOriginalValues({ ...formValues });
        }
      });
    } else {
      configUuidRef.current = null;
      const initialFormValues = {
        ...configurationInitialValues,
        llmProvider: "",
        isDefaultChat: false,
        isDefaultVoice: false,
        systemPrompt: DEFAULT_PROMPT,
      };
      setValues(initialFormValues);
      setOriginalValues(null);
    }
  }, [configurationId, dispatch, setValues]);

  const renderProviderSpecificFields = () => {
    if (formik.values.type === "chat") {
      return (
        <>
          <div className="mb-4">
            <label className="fw-bold mb-2">
              {formik.values.llmProvider
                ? `${
                    LLM_PROVIDERS.find(
                      (p) => p.value === formik.values.llmProvider,
                    )?.label || "LLM"
                  } API Key`
                : "LLM API Key"}{" "}
              <span className="text-danger"></span>
            </label>
            <input
              type="password"
              className={`form-control ${
                formik.touched.llmApiKey && formik.errors.llmApiKey
                  ? "is-invalid"
                  : ""
              }`}
              name="llmApiKey"
              placeholder={`Enter your ${
                formik.values.llmProvider
                  ? LLM_PROVIDERS.find(
                      (p) => p.value === formik.values.llmProvider,
                    )?.label || "LLM"
                  : "LLM"
              } API key`}
              value={formik.values.llmApiKey}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.llmApiKey && formik.errors.llmApiKey && (
              <div className="invalid-feedback">{formik.errors.llmApiKey}</div>
            )}
          </div>
        </>
      );
    } else if (formik.values.type === "voice") {
      return (
        <>
          <div className="mb-4">
            <label className="fw-bold mb-2">
              ElevenLabs API Key <span className="text-danger"></span>
            </label>
            <input
              type="password"
              className={`form-control ${
                formik.touched.elevenlabsApiKey &&
                formik.errors.elevenlabsApiKey
                  ? "is-invalid"
                  : ""
              }`}
              name="elevenlabsApiKey"
              placeholder="Enter your ElevenLabs API key"
              value={formik.values.elevenlabsApiKey}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.elevenlabsApiKey &&
              formik.errors.elevenlabsApiKey && (
                <div className="invalid-feedback">
                  {formik.errors.elevenlabsApiKey}
                </div>
              )}
          </div>

          <div className="mb-4">
            <label className="fw-bold mb-2">TTS Model <span className="text-danger">*</span></label>
            <select
              className={`form-control ${
                (formik.touched.modelId || formik.submitCount > 0) &&
                formik.errors.modelId
                  ? "is-invalid"
                  : ""
              }`}
              name="modelId"
              value={formik.values.modelId || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select TTS Model</option>
              {ELEVEN_TTS_MODEL_IDS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            {(formik.touched.modelId || formik.submitCount > 0) &&
              formik.errors.modelId && (
                <div className="invalid-feedback">{formik.errors.modelId}</div>
              )}
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="fw-bold mb-0">Speed (0.7–1.2)</label>
                  <span className="badge bg-secondary">
                    {formik.values.speed}
                  </span>
                </div>
                <input
                  type="range"
                  name="speed"
                  min={0.7}
                  max={1.2}
                  step={0.01}
                  value={formik.values.speed}
                  onChange={formik.handleChange}
                  className={`form-range ${
                    (formik.touched.speed || formik.submitCount > 0) &&
                    formik.errors.speed
                      ? "is-invalid"
                      : ""
                  }`}
                  style={{ accentColor: "#6f42c1" }}
                />
                {(formik.touched.speed || formik.submitCount > 0) &&
                  formik.errors.speed && (
                    <div className="invalid-feedback d-block">
                      {formik.errors.speed}
                    </div>
                  )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="mb-4">
                <label className="fw-bold mb-2">
                  Agent Output Audio Format <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-control ${
                    (formik.touched.agentOutputAudioFormat ||
                      formik.submitCount > 0) &&
                    formik.errors.agentOutputAudioFormat
                      ? "is-invalid"
                      : ""
                  }`}
                  name="agentOutputAudioFormat"
                  value={formik.values.agentOutputAudioFormat || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">Select audio format</option>
                  {AUDIO_FORMATS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
                {(formik.touched.agentOutputAudioFormat ||
                  formik.submitCount > 0) &&
                  formik.errors.agentOutputAudioFormat && (
                    <div className="invalid-feedback">
                      {formik.errors.agentOutputAudioFormat}
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="fw-bold mb-2">User Input Audio Format <span className="text-danger">*</span></label>
            <select
              className={`form-control ${
                (formik.touched.userInputAudioFormat ||
                  formik.submitCount > 0) &&
                formik.errors.userInputAudioFormat
                  ? "is-invalid"
                  : ""
              }`}
              name="userInputAudioFormat"
              value={formik.values.userInputAudioFormat || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select audio format</option>
              {AUDIO_FORMATS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            {(formik.touched.userInputAudioFormat || formik.submitCount > 0) &&
              formik.errors.userInputAudioFormat && (
                <div className="invalid-feedback">
                  {formik.errors.userInputAudioFormat}
                </div>
              )}
          </div>

          {/* <div className="mb-4">
            <label className="fw-bold mb-2">
              Voice ID <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${
                formik.touched.voiceId && formik.errors.voiceId
                  ? "is-invalid"
                  : ""
              }`}
              name="voiceId"
              placeholder="Enter voice ID"
              value={formik.values.voiceId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.voiceId && formik.errors.voiceId && (
              <div className="invalid-feedback">{formik.errors.voiceId}</div>
            )}
          </div> */}

          {/* <div className="mb-4">
            <label className="fw-bold mb-2">
              Agent ID <small className="text-muted">(optional)</small>
            </label>
            <input
              type="text"
              className="form-control"
              name="agentId"
              placeholder="Enter ElevenLabs Agent ID (optional)"
              value={formik.values.agentId || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div> */}

          {/* <div className="row">
            <div className="col-md-6">
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="fw-bold mb-0">Stability</label>
                  <span className="badge bg-secondary">
                    {formik.values.stability}
                  </span>
                </div>
                <input
                  type="range"
                  name="stability"
                  min={0}
                  max={1}
                  step={0.01}
                  value={formik.values.stability}
                  onChange={formik.handleChange}
                  className="form-range"
                  style={{ accentColor: "#6f42c1" }}
                />
                <small className="text-muted">Controls voice consistency</small>
              </div>
            </div>

            <div className="col-md-6">
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="fw-bold mb-0">Similarity Boost</label>
                  <span className="badge bg-secondary">
                    {formik.values.similarityBoost}
                  </span>
                </div>
                <input
                  type="range"
                  name="similarityBoost"
                  min={0}
                  max={0.99}
                  step={0.01}
                  value={formik.values.similarityBoost}
                  onChange={formik.handleChange}
                  className="form-range"
                  style={{ accentColor: "#6f42c1" }}
                />
                <small className="text-muted">
                  Enhances voice similarity to the original
                </small>
              </div>
            </div>
          </div> */}
        </>
      );
    }
    return null;
  };

  const getFilteredLLMModels = () => {
    return GENERAL_LLM_MODELS.filter(
      (group) => group.provider === formik.values.llmProvider,
    );
  };

  const configType = getConfigurationType(formik.values.type);
  const isCurrentDefault =
    (configType === "chat" && originalValues?.isDefaultChat) ||
    (configType === "voice" && originalValues?.isDefaultVoice);
  return (
    <div>
      <h3 className="mb-3 mt-2 d-flex align-items-center">
        <i
          className={`wb ${Icons(
            "system-activity",
          )} fs-8 me-2 mt-1 align-middle`}
        ></i>
        Configuration Settings
      </h3>

      <form onSubmit={formik.handleSubmit}>
        <div className="mb-4">
          <label className="fw-bold mb-2">
            Configuration Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${
              formik.touched.name && formik.errors.name ? "is-invalid" : ""
            }`}
            name="name"
            placeholder="Enter configuration name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.name && formik.errors.name && (
            <div className="invalid-feedback">{formik.errors.name}</div>
          )}
        </div>

        <div className="mb-4">
          <label className="fw-bold mb-2">
            System Prompt <span className="text-danger">*</span>
          </label>
          <textarea
            className={`form-control ${
              formik.touched.systemPrompt && formik.errors.systemPrompt
                ? "is-invalid"
                : ""
            }`}
            name="systemPrompt"
            rows="6"
            placeholder="Enter system prompt"
            value={formik.values.systemPrompt}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          ></textarea>

          {formik.touched.systemPrompt && formik.errors.systemPrompt && (
            <div className="invalid-feedback">{formik.errors.systemPrompt}</div>
          )}
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-4">
              <label className="fw-bold mb-2">
                Type <span className="text-danger">*</span>
              </label>
              <select
                className={`form-control ${
                  formik.touched.type && formik.errors.type ? "is-invalid" : ""
                }`}
                name="type"
                value={formik.values.type}
                onChange={(e) => {
                  const newType = e.target.value;
                  formik.setFieldValue("type", newType);

                  formik.setFieldValue("llmProvider", "");
                  formik.setFieldValue("llmModel", "");
                  formik.setFieldValue("llmApiKey", "");
                  formik.setFieldValue("ttsModel", "");
                  formik.setFieldValue("sttModel", "");
                  formik.setFieldValue("elevenlabsApiKey", "");
                  formik.setFieldValue("agentId", "");
                  formik.setFieldValue("voiceId", "");
                  formik.setFieldValue("stability", 0.5);
                  formik.setFieldValue("similarityBoost", 0.75);
                  formik.setFieldValue("modelId", "");
                  formik.setFieldValue("voiceId", "cjVigY5qzO86Huf0OWal");
                  formik.setFieldValue("speed", 1);
                  formik.setFieldValue("agentOutputAudioFormat", "");
                  formik.setFieldValue("userInputAudioFormat", "");
                }}
                onBlur={formik.handleBlur}
                disabled={!!configurationId}
              >
                <option value="">Select Type</option>
                {TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {formik.touched.type && formik.errors.type && (
                <div className="invalid-feedback">{formik.errors.type}</div>
              )}
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-4">
              <label className="fw-bold mb-2">
                LLM Provider <span className="text-danger">*</span>
              </label>
              <select
                className={`form-control ${
                  formik.touched.llmProvider && formik.errors.llmProvider
                    ? "is-invalid"
                    : ""
                }`}
                name="llmProvider"
                value={formik.values.llmProvider}
                onChange={(e) => {
                  formik.setFieldValue("llmProvider", e.target.value);
                  formik.setFieldValue("llmModel", "");
                  formik.setFieldValue("reasoningEffort", "");
                }}
                onBlur={formik.handleBlur}
              >
                <option value="">Select LLM Provider</option>
                {LLM_PROVIDERS.map((provider) => (
                  <option key={provider.value} value={provider.value}>
                    {provider.label}
                  </option>
                ))}
              </select>
              {formik.touched.llmProvider && formik.errors.llmProvider && (
                <div className="invalid-feedback">
                  {formik.errors.llmProvider}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-4">
              <label className="fw-bold mb-2">
                LLM Model <span className="text-danger">*</span>
              </label>
              <select
                className={`form-control ${
                  formik.touched.llmModel && formik.errors.llmModel
                    ? "is-invalid"
                    : ""
                }`}
                name="llmModel"
                value={formik.values.llmModel}
                onChange={(e) => {
                  const model = e.target.value;
                  formik.handleChange(e);
                  if (!REASONING_SUPPORTED_MODELS.includes(model)) {
                    formik.setFieldValue("reasoningEffort", "");
                  } else if (!formik.values.reasoningEffort) {
                    formik.setFieldValue("reasoningEffort", "low");
                  }
                }}
                onBlur={formik.handleBlur}
                disabled={!formik.values.llmProvider}
                style={{ maxHeight: "50px", overflowY: "auto" }}
              >
                <option value="">Select LLM Model</option>
                {getFilteredLLMModels().length > 0 &&
                  getFilteredLLMModels()[0].options.map((model) => (
                    <option key={model.value} value={model.value}>
                      {model.label}
                    </option>
                  ))}
              </select>
              {formik.touched.llmModel && formik.errors.llmModel && (
                <div className="invalid-feedback">{formik.errors.llmModel}</div>
              )}
            </div>
          </div>
        </div>

        {REASONING_SUPPORTED_MODELS.includes(formik.values.llmModel) && (
          <div className="mb-4">
            <label className="fw-bold mb-2">Reasoning Effort</label>
            <select
              className="form-control"
              name="reasoningEffort"
              value={formik.values.reasoningEffort || "low"}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {REASONING_EFFORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <small className="d-block text-muted mt-1">
              Controls how much compute the model spends on complex reasoning.
            </small>
          </div>
        )}

        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="fw-bold mb-0">Temperature</label>
            <span className="badge bg-secondary">
              {formik.values.temperature}
            </span>
          </div>
          <input
            type="range"
            name="temperature"
            min={0}
            max={0.99}
            step={0.01}
            value={formik.values.temperature}
            onChange={formik.handleChange}
            className="form-range"
            style={{ accentColor: "#6f42c1" }}
          />
          <small className="text-muted">
            Lower values make the model more deterministic, higher values make
            it more creative
          </small>
        </div>

        {renderProviderSpecificFields()}

        {configType && (
          <div className="mb-4">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                name={
                  configType === "chat" ? "isDefaultChat" : "isDefaultVoice"
                }
                id={configType === "chat" ? "isDefaultChat" : "isDefaultVoice"}
                checked={
                  configType === "chat"
                    ? formik.values.isDefaultChat
                    : formik.values.isDefaultVoice
                }
                onChange={formik.handleChange}
              />
              <label
                className="form-check-label fw-bold"
                htmlFor={
                  configType === "chat" ? "isDefaultChat" : "isDefaultVoice"
                }
              >
                {(() => {
                  const formattedType =
                    configType.charAt(0).toUpperCase() + configType.slice(1);
                  return (
                    <>
                      Set as Default {`${formattedType} Configuration`}
                      {isCurrentDefault && (
                        <span className="badge bg-success ms-2">
                          Currently {`Default ${formattedType}`}
                        </span>
                      )}
                    </>
                  );
                })()}
              </label>
              <small className="d-block text-muted mt-1">
                This configuration will be used by default for this assistant.
              </small>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between">
          {configUuidRef.current && (
            <button
              className="btn btn-outline-danger"
              type="button"
              onClick={handleDelete}
            >
              <i className={`wb ${Icons("delete")} fs-8 me-2 align-middle`}></i>
              Delete
            </button>
          )}

          <button
            className="btn btn-primary"
            type="submit"
            disabled={formik.isSubmitting}
          >
            <i className={`wb ${Icons("save")} fs-8 me-2 align-middle`}></i>
            {formik.isSubmitting
              ? configUuidRef.current
                ? "Updating..."
                : "Saving..."
              : configUuidRef.current
                ? "Update Configuration"
                : "Save Configuration"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConfigurationForm;
